import React from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@eventbrite/legacy-eds.button';
import { ALIGN_RIGHT } from '@eventbrite/legacy-eds.containers/constants';
import { Icon } from '@eventbrite/legacy-eds.icon';
import { trackAnalyticsEvent } from '@eventbrite/legacy-infra.site-analytics';
import classNames from 'classnames';
import findKey from 'lodash/findKey';
import type { MenuInfoShape } from '@eventbrite/consumer-header.global-nav/types';
import { DropdownMenu } from '../DropdownMenuWithFallback';
import { DEFAULT_ICON_COLOR } from '../GlobalHeaderNavigationBar/GlobalHeaderNavigationBar';
import {
    CATEGORY_HEADER,
    ACTION_PROFILE_DROPDOWN,
    CONSUMER_DROPDOWN_HOVER_LABEL,
} from './constants';
import styles from './common.module.scss';

const GlobalHeaderDropdownMenuImage = ({ imageUrl }: { imageUrl: string }) => {
    const { formatMessage } = useIntl();
    return (
        <span className={styles.menuProfileImageContainer}>
            <img
                className={styles.menuProfileImage}
                src={imageUrl}
                alt={formatMessage({ id: 'Profile Image', defaultMessage: 'Profile Image' })}
                data-testid="consumer-header-menu-profile-image"
            />
            <div className={styles.menuProfileImageBorder} />
        </span>
    );
};

const GlobalHeaderDropdownMenuIcon = ({
    iconType,
    iconColor,
}: {
    iconType: React.ReactNode;
    iconColor: string;
}) => {
    const iconContainerClasses = classNames(styles.menuIcon, styles.menuIconUserMenu);

    return (
        <span className={iconContainerClasses} data-testid="consumer-header-menu-icon-container">
            <GlobalHeaderMenuIcon iconType={iconType} iconColor={iconColor} />
        </span>
    );
};

export const GlobalHeaderUserAvatar = (menuInfo: MenuInfoShape) => {
    const { imageUrl, iconType } = menuInfo;

    let component: JSX.Element | null = null;

    const helperTypes: {
        [key: string]: {
            test: () => boolean;
            fn: React.ComponentType<any>;
        };
    } = {
        image: {
            test: () => Boolean(imageUrl),
            fn: GlobalHeaderDropdownMenuImage,
        },
        icon: {
            test: () => Boolean(iconType),
            fn: GlobalHeaderDropdownMenuIcon,
        },
    };

    const _getType = (types: typeof helperTypes) => {
        const type = findKey(types, (value) => value.test());
        return type && types[type] ? types[type].fn : null;
    };

    const UserAvatarComponent = _getType(helperTypes);

    if (UserAvatarComponent) {
        component = <UserAvatarComponent {...menuInfo} />;
    }

    return component;
};

export const GlobalHeaderMenuIcon = ({
    iconType,
    iconColor = DEFAULT_ICON_COLOR,
}: {
    iconType: React.ReactNode;
    iconColor: string;
}) => <Icon type={iconType} size="small" color={iconColor} />;

export const DropdownComponentByType = ({
    menuInfo,
    onAdditionalMenuTextItemSelect,
    noLabel,
    onClickDropdown,
    ...additionalProps
}: {
    menuInfo: MenuInfoShape;
    onAdditionalMenuTextItemSelect?: Function;
    noLabel?: boolean;
    onClickDropdown?: Function;
    additionalProps?: object;
}) => {
    const { label, fallbackUrl, navItems, textItems, className, preventMouseOver } = menuInfo;
    const menuContainerClassName = classNames(className, styles.menuContainer);
    const trackHoverDropdownMenu = () => {
        trackAnalyticsEvent({
            category: CATEGORY_HEADER,
            action: ACTION_PROFILE_DROPDOWN,
            label: CONSUMER_DROPDOWN_HOVER_LABEL,
        });
    };
    return (
        <div
            className={menuContainerClassName}
            onMouseEnter={trackHoverDropdownMenu}
            data-testid="consumer-header-dropdown-menu"
        >
            <DropdownMenu
                {...additionalProps}
                fallbackUrl={fallbackUrl}
                navItems={navItems}
                textItems={textItems}
                dropdownAlign={ALIGN_RIGHT}
                preventMouseOver={preventMouseOver}
                onTextItemSelect={onAdditionalMenuTextItemSelect}
                onClickDropdown={onClickDropdown}
                hideDropIconWhenNarrow={true}
            >
                {GlobalHeaderUserAvatar(menuInfo)}
                {!noLabel && (
                    <span className={styles.menuLabel} data-testid="consumer-header-menu-label">
                        {label}
                    </span>
                )}
            </DropdownMenu>
        </div>
    );
};

export const DropdownButton = ({
    item,
    iconType,
    iconColor,
    onClick,
}: {
    item: {
        value: string;
        content: JSX.Element;
    };
    iconType: React.ReactNode;
    iconColor: string;
    narrowIconColor?: string;
    narrowIconType?: React.ReactNode;
    onClick: Function;
}) => {
    let component: JSX.Element | null = null;
    const { value, content } = item;

    let icon: JSX.Element | null = null;

    if (iconType) {
        icon = (
            <span className={styles.dropdownButtonMenuIcon}>
                <GlobalHeaderMenuIcon iconType={iconType} iconColor={iconColor} />
            </span>
        );
    }

    component = (
        <div
            className={styles.dropdownButtonMenuButton}
            data-testid="consumer-header-dropdown-button-container"
        >
            <Button
                style="none"
                onClick={onClick.bind(null, value, 0)}
                data-testid="consumer-header-dropdown-button"
            >
                {icon}
                <span className={styles.dropdownButtonMenuLabel}>{content}</span>
            </Button>
        </div>
    );

    return component;
};
